/** Các field combo mà một dòng đơn có thể mang. Đơn cũ không có → undefined
 *  hoặc null (Mongoose schema Task 2 khai `default: null` nên field LUÔN có
 *  mặt trên payload với giá trị null, không phải absent). Mọi check "có combo
 *  hay không" phải coi null và undefined như nhau. */
export interface OrderItemComboFields {
  sku: string;
  combo_group_id?: string | null;
  combo_name?: string | null;
  combo_sku?: string | null;
  /** Số lượng SP này trong MỘT combo — dùng để render "2× Kem chống nắng". */
  combo_unit_qty?: number | null;
  combo_unit_count?: number | null;
  combo_unit_price?: number | null;
  combo_retail_total?: number | null;
}

export interface ComboOrderRow<T> {
  key: string;
  kind: 'combo' | 'loose';
  combo_name?: string;
  combo_sku?: string;
  unit_count: number;
  combo_total: number;
  retail_total: number;
  saving: number;
  members: T[];
}

/**
 * Gom dòng đơn thành hàng hiển thị: mỗi cụm combo một hàng (bung ra members),
 * dòng thường mỗi cái một hàng. Giữ nguyên thứ tự xuất hiện đầu tiên.
 */
export function groupOrderItemsByCombo<T extends OrderItemComboFields>(
  items: T[],
): ComboOrderRow<T>[] {
  const rows: ComboOrderRow<T>[] = [];
  const seen = new Set<string>();

  items.forEach((item, index) => {
    // `== null` bắt cả null lẫn undefined — bắt buộc vì đơn cũ trả
    // combo_group_id: null (không phải absent), xem comment trên interface.
    if (item.combo_group_id == null) {
      rows.push({
        key: `${item.sku}-${index}`,
        kind: 'loose',
        unit_count: 1,
        combo_total: 0,
        retail_total: 0,
        saving: 0,
        members: [item],
      });
      return;
    }
    if (seen.has(item.combo_group_id)) return;
    seen.add(item.combo_group_id);

    const members = items.filter((candidate) => candidate.combo_group_id === item.combo_group_id);
    // `?? 1` chỉ đỡ null/undefined — một `combo_unit_count: 0` (hoặc âm, dữ liệu
    // hỏng) lọt qua sẽ khiến "Đơn giá" ở detail.tsx tính combo_total / unit_count
    // = x/0. formatVND nuốt NaN/Infinity thành "0 ₫" một cách im lặng thay vì
    // báo lỗi, nên chặn ngay tại nguồn: coi mọi giá trị <= 0 như absent.
    const unitCount = item.combo_unit_count && item.combo_unit_count > 0 ? item.combo_unit_count : 1;
    const comboTotal = (item.combo_unit_price ?? 0) * unitCount;
    const retailTotal = (item.combo_retail_total ?? 0) * unitCount;

    rows.push({
      key: item.combo_group_id,
      kind: 'combo',
      combo_name: item.combo_name ?? undefined,
      combo_sku: item.combo_sku ?? undefined,
      unit_count: unitCount,
      combo_total: comboTotal,
      retail_total: retailTotal,
      saving: retailTotal - comboTotal,
      members,
    });
  });

  return rows;
}
